import React, { useEffect, FC } from 'react';
import { Provider, useDispatch } from 'react-redux';
import { BrowserRouter, withRouter, RouteComponentProps } from 'react-router-dom';
import { LocalizeProviderWrapper } from '@tradingblock/components';
import { useLocalStorage } from '@tradingblock/storage';
import { DataStore } from './state/store';
import { useApi, useApplication } from './context';
import { delayedScrollTo } from './services';
import { PageHeader, PageFooter } from './components';
import { ApplicationRouter } from './ApplicationRouter';
import { useData } from './state/selectors';
import { useDispatcher } from './components/hooks';
import { TokenActions, UiActions } from './state/actions';
import './App.css';
import config from './config';
import ReactGA from 'react-ga4';
import { ApplicationType } from './types';
import AccountApplicationWebSocketInitializer from './classes/AccountApplicationsWebSocketInitilizer';
import ExpiringTokensWebSocketInitializer from './classes/ExpiringTokensWebSocketInitilizer';
import { useFeatureFlag } from '@tradingblock/components';
import { FeatureFlagWrapper } from './components/FeatureFlagWrapper';
import WebSocketServiceInitilizer from './classes/WebSocketServiceInitilizer';

// Use vt tracking code if virtual
const TRACKING_ID = config.isVirtual ? config.ga4VTTrackingCode : config.ga4TrackingCode;
if (TRACKING_ID) {
  ReactGA.initialize(TRACKING_ID);
}
const TokenHandler: FC<{}> = () => {
  const isExpired = useData(d => d.ui.storageTokenExpired);
  const dispatch = useDispatch();
  useEffect(() => {
    if (isExpired) {
      console.log('token is expired');
      dispatch(UiActions.setNextPage('/expired'));
    }
  }, [isExpired]);
  return <></>;
};

const AppContent: React.FC<RouteComponentProps> = ({ history }) => {
  const dispatch = useDispatcher();
  const api = useApi();
  const [{ accountType }, { setNavMenuOpen, setAccountType }] = useApplication();

  // Use history.location.pathname instead of window.location.pathname.
  // history.location.pathname is relative to basename (without branch prefix)
  // window.location.pathname includes the full path with branch prefix
  const currentPage = history.location.pathname;
  const nextPage = useData(d => d.ui.nextPage);
  const application = useData(d => d.data.application);

  useEffect(() => {
    if (application && application.type !== accountType) {
      setAccountType(application.type);
    } else if (!accountType) {
      setAccountType(ApplicationType.Individual);
    }
  }, [application]);

  useEffect(() => {
    // Allow pages that do not need auth tokens to be not tested for expired token...
    if (
      currentPage !== '/' &&
      currentPage !== '/home' &&
      currentPage !== '/apply/secure' &&
      !currentPage.startsWith('/apply/unsupported/country') &&
      currentPage !== '/auth' &&
      currentPage === nextPage
    ) {
      api.user
        .profile()
        .then(result => {
          const isAuthFailure =
            result.responseCode === 401 ||
            result.responseCode === 403 ||
            result.responseCode === 200; // APINotAuthorizedResponseCode
          if (isAuthFailure) {
            dispatch(TokenActions.uiTokenExpired());
          }
        })
        .catch(err => {
          const status = err && err.response && err.response.status;
          if (status === 401 || status === 403) {
            dispatch(TokenActions.uiTokenExpired());
          } else {
            console.warn('[App] Profile check failed (non-auth error):', (err && err.message) || err);
          }
        });
    }
    if (nextPage) {
      // once arriving on nextPage, reset nextPage in state
      if (currentPage === nextPage) {
        dispatch(UiActions.clearNextPage());
      } else {
        // send to next page
        const redirectTo = nextPage;
        window.setTimeout(() => {
          history.push(redirectTo);
          delayedScrollTo(0, 0);
        }, 1);
      }
    }
  }, [currentPage, nextPage, dispatch, history]);

  const onMenuClose = () => {
    setNavMenuOpen(false);
  };

  const { isEnabled: useLofiAccountApplication } = useFeatureFlag('lofi-account-application');
  const { isEnabled: useLofiExpiringTokens } = useFeatureFlag('lofi-expiring-tokens');

  return (
    <div className={config.isVirtual ? 'env-virtual' : ''}>
      <PageHeader />
      <TokenHandler />
      <WebSocketServiceInitilizer />
      {useLofiAccountApplication && <AccountApplicationWebSocketInitializer />}
      {useLofiExpiringTokens && <ExpiringTokensWebSocketInitializer />}
      <main onClick={onMenuClose}>
        <ApplicationRouter />
      </main>
      <footer className="footer" onClick={onMenuClose}>
        <PageFooter />
      </footer>
    </div>
  );
};

const AppContentWithRouter = withRouter(AppContent);

const App: React.FC = () => {
  useEffect(() => {
    // Use window.location.pathname for GA (includes full path with branch prefix).
    ReactGA.pageview(window.location.pathname + window.location.search);
  }, []);

  return (
    <Provider store={DataStore}>
      <FeatureFlagWrapper>
        <useLocalStorage.Provider>
          <LocalizeProviderWrapper>
            <useApplication.Provider>
              <BrowserRouter basename={config.subdirectory}>
                <AppContentWithRouter />
              </BrowserRouter>
            </useApplication.Provider>
          </LocalizeProviderWrapper>
        </useLocalStorage.Provider>
      </FeatureFlagWrapper>
    </Provider>
  );
};

export default App;
